import { useState, useEffect, useRef, useMemo } from 'react';
import { useObjectsByType, useCreateObject } from '@/features/objects/hooks';
import type { Module } from '@/types/modules';
import { EditableModuleTitle } from '../_infrastructure/EditableModuleTitle';

import { useSession } from '@/features/auth/hooks/useSession';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { fetchConversationSnapshot, syncConversationModules } from '@/features/conversations/lib/conversationAdapter';
import { AttendeeChips } from './AttendeeChips';
import { VideoConferenceField } from './VideoConferenceField';
import '../_infrastructure/ModuleStyles.css';

interface TimelineModuleProps {
  module: Module;
  conversationId?: string;
  filterText?: string | null;
  [legacyProp: string]: unknown;
}

// Generate time options in 15-minute intervals
const TIME_OPTIONS = (() => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
      options.push({ value, label });
    }
  }
  return options;
})();

export default function TimelineModule(props: TimelineModuleProps) {
  const { module, filterText, conversationId: explicitConversationId } = props;
  const conversationId = explicitConversationId ?? '';
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventTime, setNewEventTime] = useState('');
  const [newEventType, setNewEventType] = useState<'activity' | 'flight' | 'hotel' | 'restaurant' | 'meeting' | 'birthday'>('activity');
  // Flight-specific fields
  const [newCarrier, setNewCarrier] = useState('');
  const [newFlightNumber, setNewFlightNumber] = useState('');
  const [newSeat, setNewSeat] = useState('');
  const [newConfirmationNumber, setNewConfirmationNumber] = useState('');
  const [newDepartsFrom, setNewDepartsFrom] = useState('');
  const [newArrivesAt, setNewArrivesAt] = useState('');
  const [newNotes, setNewNotes] = useState('');
  // Attendees & conferencing
  const [newAttendees, setNewAttendees] = useState<Array<{ email: string; displayName?: string; responseStatus?: string; photoUrl?: string; organizer?: boolean; self?: boolean }>>([]);
  const [newSendInvitations, setNewSendInvitations] = useState(true);
  const [newCreateGoogleMeet, setNewCreateGoogleMeet] = useState(false);
  const [newCustomConferenceUrl, setNewCustomConferenceUrl] = useState('');
  // Arrival date/time for paired landing event
  const [newArrivalDate, setNewArrivalDate] = useState('');
  const [newArrivalTime, setNewArrivalTime] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const gearButtonRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<{ startEdit: () => void }>(null);
  const ITEMS_PER_PAGE = 8;

  // Listen for mobile module options open event
  useEffect(() => {
    const handleOpenOptions = (event: CustomEvent) => {
      const { moduleId } = event.detail || {};
      if (moduleId === module.id) {
        setShowOptionsMenu(true);
      }
    };
    window.addEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
    return () => window.removeEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
  }, [module.id]);

  const { token } = useSession();
  const queryClient = useQueryClient();
  const workspaceId = getActiveWorkspaceId();

  // Fetch events from API
  const { data: eventsData, isLoading } = useObjectsByType<Event>('event', { moduleId: module.id, limit: 100 });
  // #2725: stale-while-revalidate — only treat as "initial load" when we have
  // no cached data yet. `useObjectsByType` in hooks.ts is configured with
  // `placeholderData` + 5-min `gcTime` for the event type, so `eventsData`
  // stays populated across refetches and the timeline never flashes a
  // "Loading events..." placeholder mid-session.
  const createEvent = useCreateObject<any, Event>('event', undefined, module.id);

  const allEvents = eventsData?.items ?? [];

  const contactFilterId = useMemo(() => {
    const normalized = filterText?.trim();
    if (!normalized || !normalized.toLowerCase().startsWith('contact:')) return null;
    const token = normalized.slice('contact:'.length);
    const [id] = token.split('|');
    return id?.trim() || null;
  }, [filterText]);

  // Filter events to only show those explicitly linked to this module.
  // #2724 review: TimelineModule does NOT apply the "hide Xavior events when
  // primary calendar is unchecked" gating that EventListModule does. That's
  // intentional — the module-link filter below is strict: every event we
  // render here has an explicit link to *this* module's id, which is itself
  // user-curated. The primary-calendar gate exists in EventListModule only
  // because that surface unions Google-pulled events across many calendars
  // for the same module and needs a way to hide the Xavior-only subset; the
  // timeline view doesn't union, so the gate has no failure mode to prevent.
  const events = useMemo(() => {
    const normalizedFilter = filterText?.trim().toLowerCase();
    const filtered = allEvents.filter((event: any) => {
      // Legacy single moduleId support
      if (event.moduleId) return event.moduleId === module.id;

      // moduleIds array (from backend normalization)
      if (event.moduleIds?.length) {
        return event.moduleIds.includes(module.id);
      }

      // moduleLinks array support - require explicit link
      return event.moduleLinks?.some((link: any) => link.moduleId === module.id);
    });
    let filteredByText = filtered;

    if (contactFilterId) {
      filteredByText = filtered.filter((event: any) => {
        const links = Array.isArray(event.links)
          ? event.links.map((value: any) => String(value))
          : Array.isArray(event.attributes?.links)
            ? event.attributes.links.map((value: any) => String(value))
            : [];
        return links.includes(contactFilterId);
      });
    } else if (normalizedFilter) {
      filteredByText = filtered.filter((event: any) => {
        const attributeText = event.attributes ? JSON.stringify(event.attributes) : '';
        const attendees = Array.isArray(event.attendees) ? event.attendees.join(' ') : '';
        const haystack = [
          event.title,
          event.name,
          event.summary,
          event.description,
          event.location,
          attendees,
          attributeText,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedFilter);
      });
    }
    // Sort events by start date
    return filteredByText.sort((a, b) =>
      new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );
  }, [allEvents, contactFilterId, filterText, module.id]);

  // Calculate pagination
  const totalPages = Math.ceil(events.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentEvents = events.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const handleBack = () => {
    setSelectedEvent(null);
    setIsCreating(false);
  };

  const handleNewEvent = () => {
    setIsCreating(true);
    setNewEventName('');
    setNewEventDate('');
    setNewEventTime('');
    setNewEventType('activity');
    // Reset flight fields
    setNewCarrier('');
    setNewFlightNumber('');
    setNewSeat('');
    setNewConfirmationNumber('');
    setNewDepartsFrom('');
    setNewArrivesAt('');
    setNewNotes('');
    setNewArrivalDate('');
    setNewArrivalTime('');
  };

  const handleCreateEvent = async () => {
    if (newEventName.trim() && newEventDate) {
      // Build flight-specific fields
      const flightFields: any = {};
      if (newEventType === 'flight') {
        if (newCarrier) flightFields.carrier = newCarrier;
        if (newFlightNumber) flightFields.flight_number = newFlightNumber;
        if (newSeat) flightFields.seat = newSeat;
        if (newConfirmationNumber) flightFields.confirmation_number = newConfirmationNumber;
        if (newDepartsFrom) flightFields.departs_from = newDepartsFrom;
        if (newArrivesAt) flightFields.arrives_at = newArrivesAt;
        if (newNotes) flightFields.description = newNotes;
      }

      // Generate a unique flight_pair_id for linking departure and landing events
      const flightPairId = newEventType === 'flight' ? `flight-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : undefined;

      // Create departure event
        const departureData: any = {
          name: newEventType === 'flight'
            ? `✈️ Depart ${newDepartsFrom || ''} → ${newArrivesAt || ''}`.trim()
            : newEventName.trim(),
          start_date: newEventDate,
          start_time: newEventTime || undefined,
          event_type: newEventType,
          moduleLinks: [{ moduleId: module.id }],
          ...flightFields,
          // Attendees & conferencing
          ...(newAttendees.length > 0 ? { attendees: newAttendees } : {}),
          ...(newCustomConferenceUrl ? { customConferenceUrl: newCustomConferenceUrl } : {}),
          ...(newSendInvitations === false ? { sendInvitations: false } : {}),
          ...(newCreateGoogleMeet ? { createGoogleMeet: true } : {}),
        };

      if (flightPairId) {
        departureData.flight_pair_id = flightPairId;
        departureData.flight_leg = 'departure';
      }

      createEvent.mutate(departureData, {
        onSuccess: () => {

          // If this is a flight with arrival info, create the landing event
          if (newEventType === 'flight' && newArrivalDate) {
            const landingData: any = {
              name: `🛬 Land at ${newArrivesAt || 'destination'}`,
              start_date: newArrivalDate,
              start_time: newArrivalTime || undefined,
              event_type: 'flight',
              moduleLinks: [{ moduleId: module.id }],
              ...flightFields,
              flight_pair_id: flightPairId,
              flight_leg: 'landing',
            };

            createEvent.mutate(landingData, {
              onSuccess: () => {
              },
              onError: (error) => {
                console.error('[TimelineModule] Failed to create landing event:', error);
              },
            });
          }

          // Reset form
          setIsCreating(false);
          setNewEventName('');
          setNewEventDate('');
          setNewEventTime('');
          setNewCarrier('');
          setNewFlightNumber('');
          setNewSeat('');
          setNewConfirmationNumber('');
          setNewDepartsFrom('');
          setNewArrivesAt('');
          setNewNotes('');
          setNewAttendees([]);
          setNewSendInvitations(true);
          setNewCreateGoogleMeet(false);
          setNewCustomConferenceUrl('');
          setNewArrivalDate('');
          setNewArrivalTime('');
        },
        onError: (error) => {
          console.error('[TimelineModule] Failed to create event:', error);
        },
      });
    } else {
      console.warn('[TimelineModule] Create failed: Name or Date missing');
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewEventName('');
    setNewEventDate('');
    setNewEventTime('');
    setNewCarrier('');
    setNewFlightNumber('');
    setNewSeat('');
    setNewConfirmationNumber('');
    setNewDepartsFrom('');
    setNewArrivesAt('');
    setNewNotes('');
    setNewArrivalDate('');
    setNewArrivalTime('');
  };

  const handleEditTitle = () => {
    setShowOptionsMenu(false);
    if (titleRef.current) {
      titleRef.current.startEdit();
    }
  };

  // Handle gear button click and calculate menu position
  const handleGearClick = () => {
    if (!showOptionsMenu && gearButtonRef.current) {
      const rect = gearButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right + 4
      });
    }
    setShowOptionsMenu(!showOptionsMenu);
  };

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showOptionsMenu) return;

      const target = event.target as HTMLElement;

      // Don't close if clicking the gear button
      if (gearButtonRef.current?.contains(target)) return;

      // Don't close if clicking the options menu dropdown
      if (optionsMenuRef.current?.contains(target)) return;

      // Close the menu for any other click
      setShowOptionsMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptionsMenu]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getEventIcon = (eventType?: string) => {
    switch (eventType) {
      case 'meeting': return '📅';
      case 'flight': return '✈️';
      case 'hotel': return '🏨';
      case 'restaurant': return '🍽️';
      case 'activity': return '🎯';
      case 'birthday': return '🎂';
      default: return '📍';
    }
  };

  if (isLoading && !eventsData) {
    return (
      <div className="module">
        <div className="module-accent-rail" style={{ background: 'var(--color-border)' }} />
        <div className="module-content" aria-busy="true" />
      </div>
    );
  }

  // Creating new event view
  if (isCreating) {
    return (
      <div className="module">
        <div className="module-accent-rail" style={{ background: 'var(--color-border)' }} />
        <div className="module-header">
          <button
            onClick={handleCancelCreate}
            style={{
              background: 'none',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              padding: '4px',
              paddingLeft: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            title="Cancel"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <button
            onClick={handleCreateEvent}
            disabled={!newEventName.trim() || !newEventDate}
            style={{
              background: 'none',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              opacity: !newEventName.trim() || !newEventDate ? 0.3 : 1,
            }}
            title="Create event"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </div>
        <div className="module-content">
          <div className="event-form">
            {/* Event Type - FIRST */}
            <div className="form-group">
              <label htmlFor="event-type">Event Type</label>
              <select
                id="event-type"
                className="form-select"
                value={newEventType}
                onChange={(e) => setNewEventType(e.target.value as typeof newEventType)}
                autoFocus
              >
                <option value="activity">🎯 Activity</option>
                <option value="meeting">📅 Meeting</option>
                <option value="flight">✈️ Flight</option>
                <option value="hotel">🏨 Hotel</option>
                <option value="restaurant">🍽️ Restaurant</option>
                <option value="birthday">🎂 Birthday</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="event-name">Event Name</label>
              <input
                id="event-name"
                type="text"
                className="form-input"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                placeholder="e.g., Team Dinner, Flight to Tokyo"
              />
            </div>

            {/* Flight-specific fields */}
            {newEventType === 'flight' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="carrier">Carrier</label>
                    <input
                      id="carrier"
                      type="text"
                      className="form-input"
                      value={newCarrier}
                      onChange={(e) => setNewCarrier(e.target.value)}
                      placeholder="e.g., United, Delta"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="flight-number">Flight #</label>
                    <input
                      id="flight-number"
                      type="text"
                      className="form-input"
                      value={newFlightNumber}
                      onChange={(e) => setNewFlightNumber(e.target.value)}
                      placeholder="e.g., UA123"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="departs-from">Departs From</label>
                    <input
                      id="departs-from"
                      type="text"
                      className="form-input"
                      value={newDepartsFrom}
                      onChange={(e) => setNewDepartsFrom(e.target.value)}
                      placeholder="e.g., SFO, LAX"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="arrives-at">Arrives At</label>
                    <input
                      id="arrives-at"
                      type="text"
                      className="form-input"
                      value={newArrivesAt}
                      onChange={(e) => setNewArrivesAt(e.target.value)}
                      placeholder="e.g., JFK, NRT"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="seat">Seat</label>
                    <input
                      id="seat"
                      type="text"
                      className="form-input"
                      value={newSeat}
                      onChange={(e) => setNewSeat(e.target.value)}
                      placeholder="e.g., 12A"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmation">Confirmation #</label>
                    <input
                      id="confirmation"
                      type="text"
                      className="form-input"
                      value={newConfirmationNumber}
                      onChange={(e) => setNewConfirmationNumber(e.target.value)}
                      placeholder="e.g., ABC123"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="flight-notes">Notes (optional)</label>
                  <textarea
                    id="flight-notes"
                    className="form-input"
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="e.g., Window seat preference, meal requirements..."
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Attendees */}
            <div className="form-group">
              <label>Attendees</label>
              <AttendeeChips
                attendees={newAttendees}
                onChange={setNewAttendees}
                sendInvitations={newSendInvitations}
                onSendInvitationsChange={setNewSendInvitations}
              />
            </div>

            {/* Video Conferencing */}
            <div className="form-group">
              <label>Video call</label>
              <VideoConferenceField
                createGoogleMeet={newCreateGoogleMeet}
                onCreateGoogleMeetChange={setNewCreateGoogleMeet}
                customConferenceUrl={newCustomConferenceUrl}
                onCustomConferenceUrlChange={setNewCustomConferenceUrl}
              />
            </div>

            {/* Date/Time fields - labeled as Departure for flights */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="event-date">{newEventType === 'flight' ? 'Departure Date' : 'Date'}</label>
                <input
                  id="event-date"
                  type="date"
                  className="form-input"
                  value={newEventDate}
                  onChange={(e) => {
                    setNewEventDate(e.target.value);
                    // Auto-set arrival date to same day if not set
                    if (newEventType === 'flight' && !newArrivalDate) {
                      setNewArrivalDate(e.target.value);
                    }
                  }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="event-time">{newEventType === 'flight' ? 'Departure Time' : 'Time (optional)'}</label>
                <select
                  id="event-time"
                  className="form-input"
                  value={newEventTime}
                  onChange={(e) => setNewEventTime(e.target.value)}
                >
                  <option value="">No time</option>
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Arrival Date/Time - only for flights */}
            {newEventType === 'flight' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="arrival-date">Arrival Date</label>
                  <input
                    id="arrival-date"
                    type="date"
                    className="form-input"
                    value={newArrivalDate}
                    onChange={(e) => setNewArrivalDate(e.target.value)}
                    min={newEventDate}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="arrival-time">Arrival Time</label>
                  <select
                    id="arrival-time"
                    className="form-input"
                    value={newArrivalTime}
                    onChange={(e) => setNewArrivalTime(e.target.value)}
                  >
                    <option value="">No time</option>
                    {TIME_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Detail view for selected event
  if (selectedEvent) {
    return (
      <div className="module">
        <div className="module-accent-rail" style={{ background: 'var(--color-border)' }} />
        <div className="module-header">
          <button className="back-btn" onClick={handleBack}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
        </div>
        <div className="module-content">
          <div className="event-detail">
            <div className="event-detail-header">
              <span className="event-icon-large">{getEventIcon(selectedEvent.event_type)}</span>
              <div className="event-meta">
                <div className="event-date-large">
                  {formatDate(selectedEvent.start_date)}
                  {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && (
                    <span> – {formatDate(selectedEvent.end_date)}</span>
                  )}
                </div>
                {selectedEvent.start_time && (
                  <div className="event-time">
                    {selectedEvent.start_time}
                    {selectedEvent.end_time && <span> – {selectedEvent.end_time}</span>}
                  </div>
                )}
              </div>
            </div>
            {selectedEvent.description && (
              <div className="event-description">
                <h4>Description</h4>
                <p>{selectedEvent.description}</p>
              </div>
            )}
            {selectedEvent.attributes && Object.keys(selectedEvent.attributes).length > 0 && (
              <div className="event-attributes">
                <h4>Details</h4>
                {Object.entries(selectedEvent.attributes).map(([key, value]) => (
                  <div key={key} className="event-attribute">
                    <span className="attribute-key">{key.replace(/_/g, ' ')}:</span>
                    <span className="attribute-value">{String(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="module">
      <div className="module-accent-rail" style={{ background: 'var(--color-border)' }} />
      <div className="module-header">
        <EditableModuleTitle
          ref={titleRef}
          module={module}
          conversationId={conversationId}
          fallbackLabel="Timeline"
        />
        <div className="module-header-actions">
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                title="Previous"
              >
                ←
              </button>
              <span className="pagination-info">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages - 1}
                title="Next"
              >
                →
              </button>
            </div>
          )}
          <button
            onClick={handleNewEvent}
            style={{
              background: 'none',
              border: '1px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              padding: '6px 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-surface-hover)';
              e.currentTarget.style.borderColor = 'var(--color-border-strong)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.borderColor = 'transparent';
            }}
            title="Add event"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Options Menu Dropdown - rendered outside module with fixed positioning */}
      {showOptionsMenu && menuPosition && (
        <div
          ref={optionsMenuRef}
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            boxShadow: 'var(--popover-shadow, 0 4px 6px rgba(0, 0, 0, 0.1))',
            minWidth: '100px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
          }}
        >
          <button
            onClick={handleEditTitle}
            style={{
              width: '100%',
              height: '30px',
              padding: '0 10px',
              background: 'none',
              border: 'none',
              borderBottom: '1px solid var(--color-border)',
              cursor: 'pointer',
              fontSize: '15px',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '6px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Rename
          </button>
          <button
            onClick={async () => {
              setShowOptionsMenu(false);
              if (!confirm('Are you sure you want to archive this module?')) return;
              if (!workspaceId || !conversationId) return;

              try {
                const snapshot = await fetchConversationSnapshot(token ?? null, workspaceId, conversationId, {
                  includeArchivedModules: true,
                });
                const modules = snapshot.conversation.modules ?? [];
                const moduleIndex = modules.findIndex((m: any) => m.id === module.id);
                if (moduleIndex === -1) return;

                const updatedModules = [...modules];
                updatedModules[moduleIndex] = {
                  ...modules[moduleIndex],
                  archived: true,
                };

                await syncConversationModules(token ?? null, workspaceId, conversationId, updatedModules);

                queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
                queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
              } catch (err) {
                console.error('[TimelineModule] Failed to archive module:', err);
                alert('Failed to archive module');
              }
            }}
            style={{
              width: '100%',
              height: '30px',
              padding: '0 10px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '15px',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '6px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="21 8 21 21 3 21 3 8"></polyline>
              <rect x="1" y="3" width="22" height="5"></rect>
              <line x1="10" y1="12" x2="14" y2="12"></line>
            </svg>
            Archive
          </button>
        </div>
      )}

      <div className="module-content">
        {events.length === 0 ? (
          <div className="text-center text-gray-500 py-8">Add when you're ready</div>
        ) : (
          <div className="timeline-list">
            {currentEvents.map((event, index) => (
              <div
                key={event.id}
                className="timeline-item clickable"
                onClick={() => handleEventClick(event)}
              >
                <div className="timeline-marker">
                  <div className="timeline-dot"></div>
                  {index < currentEvents.length - 1 && <div className="timeline-line"></div>}
                </div>
                <div className="timeline-content">
                  <div className="timeline-date">
                    {formatDate(event.start_date)}
                    {event.end_date && event.end_date !== event.start_date && (
                      <span> – {formatDate(event.end_date)}</span>
                    )}
                  </div>
                  <div className="timeline-event">
                    <span className="timeline-icon">{getEventIcon(event.event_type)}</span>
                    <span className="timeline-name">{event.name}</span>
                    {event.start_time && (
                      <span className="timeline-time">
                        {event.start_time}
                        {event.end_time && <span> – {event.end_time}</span>}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
